import React from 'react';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldError from './FieldError';
import { twJoin } from 'tailwind-merge';
import FieldLabel from './FieldLabel';
import TextareaAutosize, {
  TextareaAutosizeProps,
} from 'react-textarea-autosize';
import FieldDescribe from './FieldDescribe';
import InputLength from './InputLength';

type ControlledTextareaProps<T extends FieldValues> = {
  name: Path<T>;
  label?: string | React.ReactNode;
  describe?: React.ReactNode;
  isRequired?: boolean;
  startHeight?: number;
  withLength?: boolean;
  wrapperClassName?: string;
} & Omit<
  TextareaAutosizeProps,
  'name' | 'onBlur' | 'onChange' | 'ref' | 'value' | 'onHeightChange'
>;

const FormTextarea = <TFormValues extends FieldValues>({
  name,
  label,
  className,
  wrapperClassName,
  isRequired,
  describe,
  withLength,
  maxLength,
  ...props
}: ControlledTextareaProps<TFormValues>) => {
  const { register, control } = useFormContext<TFormValues>();

  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  return (
    <div className={twJoin('flex flex-col gap-0.5', wrapperClassName)}>
      <FieldLabel name={name} label={label} isRequired={isRequired} />
      <TextareaAutosize
        id={name}
        {...register(name)}
        {...props}
        maxLength={maxLength}
        className={twJoin(
          'resize-none scrollbar-thin scrollbar-thumb-rounded-full scrollbar-track-rounded-full min-h-12 placeholder-med-gray-3 grow p-3 text-dark-gray font-semibold text-sm outline-none border border-med-gray-3 rounded-5 focus:outline-none placeholder:font-medium focus:border-blue-600',
          className,
          error && '!border-alert-negative bg-error-yellow',
        )}
        minRows={1}
      />
      {error && <FieldError>{error?.message as string}</FieldError>}
      {!error && describe && <FieldDescribe>{describe}</FieldDescribe>}
      {!error && withLength && maxLength && (
        <InputLength
          maxLength={maxLength}
          currentLength={field?.value?.length ?? 0}
        />
      )}
    </div>
  );
};

export default FormTextarea;
